import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { Fade, Grid, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useSnackbar } from 'notistack';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import { blue, green, red } from '@mui/material/colors';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import ProcessGuide from '../../components/orden/ProcessGuide';

const CHIP_DEFS = [
    { key: 'todos', label: 'Todos', color: 'primary' },
    { key: 'activas', label: 'Activas', color: 'success' },
    { key: 'inactivas', label: 'Inactivas', color: 'default' },
    { key: 'con_insumo', label: 'Con insumo', color: 'warning' },
    { key: 'sin_insumo', label: 'Sin insumo', color: 'info' },
];

function formatMinutos(min) {
    if (min == null) return '—';
    const m = Number(min);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    if (h < 48) return rest ? `${h} h ${rest} min` : `${h} h`;
    const d = Math.floor(h / 24);
    return `${d} d`;
}

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [selectedChip, setSelectedChip] = useState('todos');
    const [counts, setCounts] = useState({});
    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const chipItems = useMemo(
        () => CHIP_DEFS.map((c) => ({ ...c, count: counts[c.key] != null ? counts[c.key] : 0 })),
        [counts]
    );

    const onChipChange = useCallback((key) => {
        setSelectedChip(key);
        setTableMainNeedUpdate(Date.now());
    }, []);

    const onNuevo = useCallback(() => {
        globalsContext.setIdEdit(false);
        navigate('/practica/nuevo');
    }, [globalsContext, navigate]);

    const onEditar = useCallback((row) => {
        globalsContext.setIdEdit(row.id);
        navigate('/practica/editar');
    }, [globalsContext, navigate]);

    const onEliminar = useCallback((row) => {
        alertModal.showModal(
            'Eliminar práctica',
            'La práctica "' + (row.nombre || '') + '" será eliminada. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await deleteData(`practicas/${row.id}`, usuario.token);
                if (response.error === '') {
                    enqueueSnackbar('Práctica eliminada correctamente', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            }
        );
    }, [usuario.token, alertModal, loadingModal, enqueueSnackbar]);

    const tableMain = useMemo(() => ({
        title: 'Prácticas',
        idName: 'id',
        defaultOrder: 'asc',
        headers: [
            { id: 'nombre', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
            {
                id: 'activo',
                label: 'Estado',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip size="small" color={row.activo ? 'success' : 'default'} label={row.activo ? 'Activa' : 'Inactiva'} />
                ),
            },
            {
                id: 'plazo_respuesta_minutos',
                label: 'Plazo respuesta',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => formatMinutos(row.plazo_respuesta_minutos),
            },
            {
                id: 'plazo_max_sin_turno_minutos',
                label: 'Máx. sin turno',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => formatMinutos(row.plazo_max_sin_turno_minutos),
            },
            {
                id: 'reprogramaciones_max',
                label: 'Reprog.',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip
                        size="small"
                        variant="outlined"
                        color="info"
                        label={`Máx. ${row.reprogramaciones_max ?? 2}`}
                        title={`Antelación: ${row.reprogramacion_antelacion_horas ?? 24} h · Intervalo: ${row.reprogramacion_cooldown_horas ?? 12} h`}
                    />
                ),
            },
            {
                id: 'requiere_insumo',
                label: 'Insumo',
                dataType: DataType.TEXT,
                sortable: true,
                customRender: (row) => (
                    <Chip
                        size="small"
                        color={row.requiere_insumo ? 'warning' : 'default'}
                        variant={row.requiere_insumo ? 'filled' : 'outlined'}
                        label={row.requiere_insumo ? `Sí (${row.plazo_compra_horas || 0} h)` : 'No'}
                    />
                ),
            },
            {
                id: 'establecimientos_count',
                label: 'Establecimientos',
                dataType: DataType.NUMBER,
                sortable: false,
            },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, _sf, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const params = new URLSearchParams();
            params.append('per_page', String(rowsPerPage));
            params.append('page', String(page + 1));
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);
            if (selectedChip && selectedChip !== 'todos') params.append('chip', selectedChip);

            const response = await getDataFull(`practicas?${params.toString()}`, usuario.token);
            if (response.error === '') {
                pageUpdate(response.meta?.page ? response.meta.page - 1 : page);
                totalUpdate(response.meta?.total || 0);
                if (filterListUpdate) filterListUpdate([]);
                if (response.counts) setCounts(response.counts);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: [],
        rowActions: (row) => [
            {
                label: 'Editar',
                secondary: 'Modificar práctica y plazos',
                icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                itemColor: blue[600],
                action: () => onEditar(row),
            },
            {
                label: 'Eliminar',
                secondary: 'Quitar del catálogo',
                icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                itemColor: red[600],
                action: () => onEliminar(row),
            },
        ],
    }), [usuario.token, selectedChip, onEditar, onEliminar, enqueueSnackbar]);

    return (
        <Layout nombrePagina="Prácticas">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <ListadoOperativoBanner
                        title="Prácticas"
                        description="Consulta el catálogo de prestaciones, plazos de respuesta e insumos. Use los filtros para priorizar activas o con compra previa."
                        icon={MedicalServicesIcon}
                        accentColor="info"
                        headerAction={(
                            <Button startIcon={<AddCircleOutlineRoundedIcon />} onClick={onNuevo}>
                                Nueva práctica
                            </Button>
                        )}
                        chips={chipItems}
                        selectedChip={selectedChip}
                        onChipChange={onChipChange}
                    />
                    <ProcessGuide
                        title="Guía rápida"
                        steps={[
                            'Los plazos de respuesta y sin turno definen el SLA al crear una orden.',
                            'Si requiere insumo, el plazo de compra (0 h = urgente) condiciona el inicio de la asignación.',
                            'Vincule establecimientos que ofrezcan la práctica para que puedan recibir órdenes.',
                        ]}
                    />
                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={10}
                        needUpdate={tableMainNeedUpdate}
                        hideFilters
                        onRowClick={(row) => onEditar(row)}
                    />
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
